import Constants from 'expo-constants';
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Linking, Platform } from 'react-native';
import Purchases from 'react-native-purchases';

import {
  resetAccountAnomalyNotice,
  useApolloClient,
} from '@hooks/useApolloClient';
import { GoogleAuthProvider } from '@hooks/useGoogleAuth';
import { FCMToken } from '@src/constants/constants';
import { resetChatReadGuards } from '@utils/chat/chatReadGuard';
import { markLoginSucceeded } from '@utils/loginSuccessMarker';
import { clearOnboardingInvitees } from '@utils/onboardingInvitedMembers';
import {
  getCurrentHref,
  getPasswordResetToken,
  isPasswordResetUrl,
} from '@utils/passwordResetLink';
import { clearPendingTeamJoinRequests } from '@utils/pendingTeamJoinRequests';
import {
  broadcastSessionEnded,
  subscribeToSessionEnded,
} from '@utils/sessionBroadcast';
import {
  INVITE_SIGNUP_START_CONVERSATION_KEY,
  INVITE_SIGNUP_TASKS_WAITING_KEY,
} from '@utils/startConversationTutorial';
import { AsyncStorage, SecurityStorage } from '@utils/storage';
import { clearPendingTeamSignup } from '@utils/teams/pendingTeamSignup';

export type RemoveTokenOptions = {
  /**
   * Whether to tell the browser's other tabs that the session ended.
   * `false` only when *reacting* to such a message, so tabs don't ping-pong.
   */
  broadcast?: boolean;
};

export type AuthContextType = {
  token: string | undefined;
  setAuthToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  registerLoading: boolean;
  setRegisterLoading: React.Dispatch<React.SetStateAction<boolean>>;
  removeToken: (options?: RemoveTokenOptions) => void;
  /** `true` while the auth context is initializing. `false` if the auth context has completed loading */
  loading: boolean;
  /**
   * TA-2593: a password-reset link was opened while this browser still held a
   * session. AppNavigator keeps AuthStack (where the reset screen lives)
   * mounted without ending the session — only a successful reset may do that.
   */
  passwordResetPending: boolean;
  /** Leave the reset screen and return a still-signed-in user to the app. */
  endPasswordReset: () => void;
  /**
   * TA-11134 (native): token from a password-reset link the app was opened
   * with. AuthStack (signed out) or AppStack (signed in, as a modal over the
   * user's screens) opens the reset form with it; the session is left alone
   * until a reset succeeds.
   */
  passwordResetToken: string | undefined;
  /** Forget the pending link (the reset form finished or was left). */
  clearPasswordReset: () => void;
};

const AuthContext = createContext<AuthContextType>({
  token: undefined,
  setAuthToken: () => null,
  registerLoading: false,
  setRegisterLoading: () => null,
  removeToken: () => null,
  loading: false,
  passwordResetPending: false,
  endPasswordReset: () => null,
  passwordResetToken: undefined,
  clearPasswordReset: () => null,
});

// The launch URL is returned for the life of the process, so a remounted
// provider (e.g. an Android activity recreation) must not reopen a reset link
// the user already finished or abandoned.
let initialResetLinkHandled = false;

const readInitialPasswordResetToken = async (): Promise<string | null> => {
  if (initialResetLinkHandled) return null;
  initialResetLinkHandled = true;
  try {
    return getPasswordResetToken(await Linking.getInitialURL());
  } catch {
    return null;
  }
};

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // NOTE: We need to distinguish between unknown token values and invalid values.
  // `undefined` is being treated as "unknown". In `getToken()`, we attempt to retrieve
  // the token from AsyncStorage. A value of `null` is considered unauthenticated.
  // Without distinguishing between "unknown" and "invalid", the cache will be cleared
  // every time the app restarts.
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  const {
    client,
    clearCache,
    recentStatusCode,
    clearRecentStatusCode,
    resetWsConnectionState,
  } = useApolloClient();
  const [loading, setLoading] = useState(true);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [passwordResetPending, setPasswordResetPending] = useState(false);
  const endPasswordReset = useCallback(
    () => setPasswordResetPending(false),
    []
  );
  const [passwordResetToken, setPasswordResetToken] = useState<
    string | undefined
  >(undefined);
  const clearPasswordReset = useCallback(
    () => setPasswordResetToken(undefined),
    []
  );

  const getToken = async () => {
    try {
      const value = await SecurityStorage.getItem('authToken');

      // TA-2593 (VAPT): `password-reset` only exists on AuthStack, so a reset
      // link opened in a browser that still holds a session could not render.
      // Flag it so AppNavigator shows AuthStack, but keep the session: the link
      // may be expired, reused or planted, and only a reset the backend accepts
      // may end it (ResetPassword.web does that on success).
      if (
        value &&
        Platform.OS === 'web' &&
        isPasswordResetUrl(getCurrentHref())
      ) {
        setPasswordResetPending(true);
      }

      // TA-11134: on native, a reset link that cold-starts the app is routed
      // to the reset form even when signed in. The session stays here too: an
      // expired, reused or forged link must not be able to log anyone out.
      if (Platform.OS !== 'web') {
        const resetToken = await readInitialPasswordResetToken();
        if (resetToken) setPasswordResetToken(resetToken);
      }

      setAuthToken(value);

      const version = Constants?.expoConfig?.version;
      const cacheClearVersion = await AsyncStorage.getItem('cacheClearVersion');

      if (version && clearCache && version !== cacheClearVersion) {
        await clearCache();
        await AsyncStorage.setItem('cacheClearVersion', version);
      }
      return value;
    } catch (e) {
      console.error(e);
      // error reading value
    }
  };

  const removeToken = useCallback(
    async (options?: RemoveTokenOptions) => {
      const broadcast = options?.broadcast ?? true;
      if (Platform.OS === 'ios') {
        try {
          // Detach the RevenueCat identity so the next login doesn't inherit
          // this user's purchases. Skip when already anonymous — logOut would
          // reject and the SDK logs a console.error (red screen in dev).
          // Isolated so an RC failure never blocks the rest of the logout cleanup.
          if (
            (await Purchases.isConfigured()) &&
            !(await Purchases.isAnonymous())
          ) {
            await Purchases.logOut();
          }
        } catch (e) {
          console.log('RevenueCat logOut failed, ', e?.toString());
        }
      }
      try {
        await SecurityStorage.deleteItem('authToken');
        await AsyncStorage.deleteItem(FCMToken);
        await AsyncStorage.deleteItem('role_tooltip_shown');
        client?.cache?.reset();
        if (Platform.OS === 'web') {
          // TA-10772: first, because the localStorage cleanup below can throw
          // (private mode, and jsdom without a storage shim) and this must not
          // be the casualty. The records are keyed by team only, so leaving
          // them behind would show the next user to sign in to this tab a
          // disabled "Pending Request" for a request they never made.
          clearPendingTeamJoinRequests();
          // TA-10886: the team a registration described is not account-bound,
          // so an unconsumed one must not survive into the next session in
          // this tab — the next user would be shown that team's checkout.
          clearPendingTeamSignup();
          // TA-10892: set by invite entry points; left behind it makes the next
          // account registered in this tab skip the welcome tutorial, which
          // gates every later onboarding tooltip.
          window.sessionStorage.removeItem('skip_welcome_modal');
          // TA-10892 #27/#33: account-independent onboarding handoffs — the
          // next account in this tab must not get this one's invitee chats or
          // invite-signup tooltip. clearOnboardingInvitees never throws.
          clearOnboardingInvitees();
          const prefix = 'checklist_onboarding_tooltip_dismissed_';
          const keysToRemove: string[] = [];
          for (let i = 0; i < (globalThis.localStorage?.length ?? 0); i++) {
            const key = globalThis.localStorage?.key(i);
            if (key?.startsWith(prefix)) keysToRemove.push(key);
          }
          keysToRemove.forEach((k) => globalThis.localStorage?.removeItem(k));
          globalThis.localStorage?.removeItem('copy_link_toast_shown');
          globalThis.localStorage?.removeItem(
            INVITE_SIGNUP_START_CONVERSATION_KEY
          );
          globalThis.localStorage?.removeItem(INVITE_SIGNUP_TASKS_WAITING_KEY);
        }
      } catch (e) {
        console.log('An error happened, ', e?.toString());
      } finally {
        setAuthToken('');
        // TA-2593: a signed-out browser reaches AuthStack anyway; left set,
        // the flag would hold the *next* login on AuthStack.
        setPasswordResetPending(false);
        // TA-9295: the mark-as-read guards are module state keyed by chat id,
        // so without this they outlive the session and could suppress an
        // unread badge for the next account signed in on this device.
        resetChatReadGuards();
        // TA-10160 (QA round 2): ApolloClientProvider outlives logout/login, so
        // its "socket has completed a handshake this session" mark must be
        // cleared here. Otherwise the next login in the same page session
        // inherits it, loses the startup grace period, and a 4401 racing the
        // token write reads as a revoked session — logging the user back out.
        resetWsConnectionState?.();
        // TA-2593 (VAPT): sibling tabs hold their own copy of the token in
        // memory, so they stay authenticated until they reload unless they are
        // told the session is over.
        if (broadcast) broadcastSessionEnded();
      }
    },
    [client, resetWsConnectionState]
  );

  const checkUserSession = async () => {
    if (Platform.OS !== 'web') {
      try {
        const alreadyInstall = await AsyncStorage.getItem('alreadyInstall');
        if (!alreadyInstall) {
          // Migration guard: if a Keychain token already exists this is an
          // existing user whose `alreadyInstall` flag lived in the old
          // AsyncStorage (pre-v3 migration). Write the flag to MMKV so we
          // don't clear their session. For a genuine fresh install the
          // Keychain will be empty and we fall through to removeToken().
          const existingToken = await SecurityStorage.getItem('authToken');
          if (existingToken) {
            await AsyncStorage.setItem('alreadyInstall', 'true');
          } else {
            removeToken();
          }
        }
      } catch (e) {
        console.error('checkUserSession storage error:', e);
      }
    }
    setLoading(true);
    getToken().finally(() => setLoading(false));
  };
  const checkKeepSignedIn = async () => {
    const existingToken = await SecurityStorage.getItem('authToken');
    if (!existingToken) return;

    const setKeepSignedIn =
      window.sessionStorage.getItem('setKeepSignedIn') ?? '0';

    if (setKeepSignedIn && setKeepSignedIn === '0') {
      const keepSignedIn =
        (await AsyncStorage.getItem('keepSignedIn')) ?? 'true';

      if (keepSignedIn && keepSignedIn !== 'true') {
        // A fresh tab of a "don't keep me signed in" session drops only its
        // own copy of the session. Broadcasting here would log out the tab
        // the user is still working in (TA-2593).
        removeToken({ broadcast: false });
      } else {
        window.sessionStorage.setItem('setKeepSignedIn', '1');
      }
    }
  };

  useEffect(() => {
    checkUserSession();
    if (Platform.OS === 'web') {
      checkKeepSignedIn();
    }
  }, []);
  const asyncClearCache = async () => {
    if (clearCache) await clearCache();
  };
  useEffect(() => {
    if (
      Platform.OS === 'web' &&
      (authToken === null || authToken === '') &&
      clearCache
    ) {
      asyncClearCache().catch(console.error);
    }
  }, [authToken]);

  // TA-11134: a reset link opened while the app is already running.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const subscription = Linking.addEventListener('url', ({ url }) => {
      const resetToken = getPasswordResetToken(url);
      if (resetToken) setPasswordResetToken(resetToken);
    });
    return () => subscription.remove();
  }, []);

  // TA-2593 (VAPT): mirror another tab's logout into this one. `broadcast:
  // false` stops the two tabs from echoing the message back and forth.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    return subscribeToSessionEnded(() => {
      if (authToken) removeToken({ broadcast: false });
    });
  }, [authToken, removeToken]);

  useEffect(() => {
    if (recentStatusCode?.code === 401) {
      // `undefined` is the still-loading state, not "signed out" — the Apollo
      // auth link reads storage on its own, so a 401 can land before this
      // resolves. Leave the signal standing; this effect re-runs once the
      // token is known and handles it then (TA-10850 QA).
      if (authToken === undefined) return;

      clearRecentStatusCode?.();
      // Nothing to log out when there is no token — a 401 from an in-flight
      // request after logout. The account-anomaly notice is reset when the
      // next session starts, not here: every late 401 makes a new signal, and
      // resetting per signal would let them notify again (TA-10850 QA).
      if (authToken) removeToken();
    }
  }, [recentStatusCode, authToken, clearRecentStatusCode, removeToken]);

  // TA-10891: record a real sign-in — the token going from none ('' or a null
  // storage read) to set. A token restored from storage on page load starts
  // from `undefined` and is deliberately not counted.
  const previousTokenRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const previous = previousTokenRef.current;
    previousTokenRef.current = authToken;
    if (authToken && (previous === '' || previous === null)) {
      markLoginSucceeded();
    }
  }, [authToken]);

  // A session is starting: the next account failure is a new auth cycle and
  // owes the user its own explanation (TA-10850 QA).
  useEffect(() => {
    // Optional call: suites that mock the Apollo module wholesale do not
    // provide this export.
    if (authToken) resetAccountAnomalyNotice?.();
  }, [authToken]);

  return (
    <AuthContext.Provider
      value={{
        token: authToken,
        setAuthToken,
        removeToken,
        loading,
        passwordResetPending,
        endPasswordReset,
        registerLoading,
        setRegisterLoading,
        passwordResetToken,
        clearPasswordReset,
      }}>
      <GoogleAuthProvider>{children}</GoogleAuthProvider>
    </AuthContext.Provider>
  );
};
